import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  HostListener,
  OnDestroy,
  OnInit,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { A11yModule } from '@angular/cdk/a11y';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { animate, style, state, transition, trigger } from '@angular/animations';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import { OutlookPanelService } from '../services/outlook-panel.service';
import { OutlookDataService } from '../services/outlook-data.service';
import {
  OutlookConnectionState,
  OutlookTab,
  OutlookTabDef
} from '../models/IOutlookPanel.interface';
import { IOutlookAccountSummary } from '../models/IOutlookProfile.interface';

import { OutlookConnectStateComponent } from '../components/outlook-connect-state/outlook-connect-state.component';
import { OutlookMailTabComponent } from '../components/outlook-mail-tab/outlook-mail-tab.component';
import { OutlookCalendarTabComponent } from '../components/outlook-calendar-tab/outlook-calendar-tab.component';
import { OutlookContactsTabComponent } from '../components/outlook-contacts-tab/outlook-contacts-tab.component';
import { OutlookProfileTabComponent } from '../components/outlook-profile-tab/outlook-profile-tab.component';
import { OutlookSignatureTabComponent } from '../components/outlook-signature-tab/outlook-signature-tab.component';

import { MsalService } from '@azure/msal-angular';

/**
 * The global Outlook panel.
 *
 * Mounted once in AppComponent and opened from the header, matching the Phonebook sidebar and the
 * Meeting Notes panel — same geometry, same animation timings, so it reads as part of the same app
 * rather than a bolted-on second thing.
 *
 * The one rule worth stating: opening this panel never triggers an interactive sign-in. It reads
 * the existing connection state, optionally attempts a strictly silent SSO, and otherwise shows a
 * Connect button. A popup that is not attached to a click gets blocked by the browser, so every
 * path that can prompt starts at one.
 */
@Component({
  selector: 'app-outlook-panel',
  imports: [
    CommonModule,
    A11yModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    OutlookConnectStateComponent,
    OutlookMailTabComponent,
    OutlookCalendarTabComponent,
    OutlookContactsTabComponent,
    OutlookProfileTabComponent,
    OutlookSignatureTabComponent
  ],
  templateUrl: './outlook-panel.component.html',
  styleUrls: ['./outlook-panel.component.scss'],
  animations: [
    trigger('slidePanel', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('300ms cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'none' }))
      ]),
      transition(':leave', [
        animate('250ms cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'translateX(100%)' }))
      ])
    ]),
    trigger('fadeBackdrop', [
      state('void', style({ opacity: 0 })),
      state('*', style({ opacity: 1 })),
      transition(':enter', animate('200ms ease-out')),
      transition(':leave', animate('150ms ease-in'))
    ])
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OutlookPanelComponent implements OnInit, OnDestroy {
  readonly panelService = inject(OutlookPanelService);
  private readonly data = inject(OutlookDataService);
  private readonly msal = inject(MsalService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  readonly tabsList: OutlookTabDef[] = [
    { key: 'mail', label: 'Mail', icon: 'mail_outline' },
    { key: 'calendar', label: 'Calendar', icon: 'event' },
    { key: 'contacts', label: 'Contacts', icon: 'contacts' },
    { key: 'profile', label: 'Profile', icon: 'account_circle' },
    { key: 'signature', label: 'Signature', icon: 'draw' }
  ];

  isPanelOpen = false;
  activeTab: OutlookTab = 'mail';
  connection: OutlookConnectionState = 'unknown';
  connecting = false;

  /** Widens the panel to make room for the mail reading pane. */
  isReadingPaneOpen = false;

  account: IOutlookAccountSummary | null = null;

  /** Guards the one-shot silent sign-in so it is not retried on every open. */
  private silentSignInTried = false;

  ngOnInit(): void {
    this.panelService.isOpen$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((open) => {
      this.isPanelOpen = open;
      if (open) {
        this.onOpened();
      }
      this.cdr.markForCheck();
    });

    this.panelService.activeTab$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((tab) => {
      this.activeTab = tab;
      this.cdr.markForCheck();
    });

    this.panelService.selectedMessageId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((id) => {
      this.isReadingPaneOpen = !!id;
      this.cdr.markForCheck();
    });

    this.data.connection$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((state) => {
      this.connection = state;
      this.account = state === 'connected' ? this.data.accountSummary() : null;
      this.cdr.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.panelService.close();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isPanelOpen) {
      this.closePanel();
    }
  }

  closePanel(): void {
    this.panelService.close();
  }

  setTab(tab: OutlookTab): void {
    this.panelService.setTab(tab);
  }

  onConnect(): void {
    this.connecting = true;
    this.cdr.markForCheck();

    this.data
      .connect()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.connecting = false;
        this.cdr.markForCheck();
      });
  }

  onRetry(): void {
    this.data.invalidate();
    this.data.refreshConnection();
  }

  onSignOut(): void {
    this.data
      .disconnect()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cdr.markForCheck());
  }

  openOutlookWeb(): void {
    window.open('https://outlook.office.com/mail/', '_blank', 'noopener');
  }

  /**
   * Runs on every open, but the expensive half runs once.
   *
   * The MSAL cache for Graph is sessionStorage, so closing every browser tab drops the account even
   * though the Microsoft session cookie usually outlives it. ssoSilent recovers exactly that case
   * without a prompt; when it fails — which it does silently — the user simply sees the Connect
   * button they would have seen anyway.
   */
  private onOpened(): void {
    this.data.refreshConnection();

    if (this.silentSignInTried || this.data.accountSummary()) {
      return;
    }
    this.silentSignInTried = true;

    // The BizCombo B2C account's username is the user's work email, which is the right login hint
    // and is available synchronously — unlike UserService.getCurrentUser(), which is an HTTP call.
    const email = this.msal.instance.getAllAccounts()[0]?.username;
    if (!email) {
      return;
    }

    this.data
      .trySilentSignIn(email)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.cdr.markForCheck());
  }
}
